<template>
  <div class="results-view">
    <!-- Header mit Gesamtergebnis -->
    <div class="results-header">
      <div class="results-icon">
        <div class="icon icon-success icon-lg icon-circle">
          <i class="fas fa-clipboard-check"></i>
        </div>
      </div>
      <h2 class="result-title">Testergebnis</h2>
      <p class="text-muted mb-0">{{ score }} von {{ maxScore }} Punkten erreicht</p>
    </div>
    
    <!-- Ergebnis-Zusammenfassung -->
    <div class="result-summary">
      <div class="row align-items-center">
        <div class="col-md-5 text-center mb-3 mb-md-0">
          <div class="result-chart">
            <div class="chart-container">
              <div class="donut-chart" :style="{ '--percentage': calculatePercentage() }">
                <div class="donut-content">
                  <div class="donut-percentage">{{ calculatePercentage() }}%</div>
                  <div class="donut-label">Erfolgsquote</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-7">
          <div class="result-stats rounded border">
            <div class="stat-item d-flex justify-content-between">
              <span>Punkte erreicht:</span>
              <span class="fw-bold text-success">{{ score }}</span>
            </div>
            <div class="stat-item d-flex justify-content-between">
              <span>Nicht erreichte Punkte:</span>
              <span class="fw-bold text-danger">{{ maxScore - score }}</span>
            </div>
            <div class="stat-item d-flex justify-content-between">
              <span>Maximale Punktzahl:</span>
              <span class="fw-bold">{{ maxScore }}</span>
            </div>
            <div class="stat-item d-flex justify-content-between">
              <span>Erfolgsquote:</span>
              <span class="fw-bold" :class="getScoreClass(calculatePercentage())">
                {{ calculatePercentage() }}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Detaillierte Fragenübersicht -->
    <div class="result-detail" v-if="showDetails">
      <h3 class="section-title mb-3">Detaillierte Fragenübersicht</h3>
      <div v-for="(question, index) in questions" :key="`result_${index}`">
        <QuestionDisplay 
          :question="question" 
          :index="index" 
          :totalQuestions="questions.length" 
          :answer="answers[index]" 
          :isReviewMode="true"
        />
      </div>
    </div>
    
    <!-- Aktionen -->
    <div class="d-flex justify-content-between mt-3">
      <slot name="actions"></slot>
    </div>
  </div>
</template>

<script>
import QuestionDisplay from '@/views/tests/components/QuestionDisplay.vue';

export default {
  name: 'TestResults',
  components: {
    QuestionDisplay
  },
  props: {
    questions: {
      type: Array,
      required: true
    },
    answers: {
      type: Array,
      required: true
    },
    score: {
      type: Number,
      required: true
    },
    maxScore: {
      type: Number,
      default() {
        return this.questions.length;
      }
    },
    percentage: {
      type: Number,
      default: null
    },
    showDetails: {
      type: Boolean,
      default: true
    }
  },
  methods: {
    calculatePercentage() {
      if (this.percentage !== null) {
        return this.percentage;
      }
      
      if (this.maxScore === 0) return 0;
      return Math.round((this.score / this.maxScore) * 100);
    },
    getScoreClass(percentage) {
      if (percentage >= 80) return 'text-success';
      if (percentage >= 60) return 'text-primary';
      if (percentage >= 40) return 'text-warning';
      return 'text-danger';
    }
  }
};
</script>

<style lang="scss" scoped>
.results-view {
  background-color: var(--card-bg, white);
  border-radius: 14px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
  padding: 1.25rem;
  
  .dark-mode & {
    background-color: var(--dark-card-bg, #1f2937);
  }
}

.results-header {
  text-align: center;
  margin-bottom: 2rem;
  
  .results-icon {
    display: inline-flex;
    margin-bottom: 1rem;
  }
  
  .result-title {
    margin-bottom: 0.5rem;
    font-weight: 700;
    font-size: 1.5rem;
  }
}

.result-summary {
  margin-bottom: 2rem;
  
  .chart-container {
    padding: 0.5rem;
  }
}

.result-stats {
  background-color: var(--bg-light, #f9fafb);
  border-radius: 12px;
  border-color: var(--border-color, #e5e7eb);
  overflow: hidden;
  
  .stat-item {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--border-color, #e5e7eb);
    font-size: 0.95rem;
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  .dark-mode & {
    background-color: var(--dark-bg-secondary, #1f2937);
    border-color: var(--dark-border-color, #374151);
    
    .stat-item {
      border-color: var(--dark-border-color, #374151);
    }
  }
}

.donut-chart {
  position: relative;
  width: 160px;
  height: 160px;
  margin: 0 auto;
  border-radius: 50%;
  background: conic-gradient(
    var(--primary-color, #3b82f6) calc(var(--percentage) * 1%),
    #e5e7eb calc(var(--percentage) * 1%)
  );
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    position: absolute;
    width: 70%;
    height: 70%;
    background-color: white;
    border-radius: 50%;
  }
  
  .donut-content {
    position: relative;
    z-index: 1;
    text-align: center;
    
    .donut-percentage {
      font-size: 1.75rem;
      font-weight: 700;
      color: var(--primary-color, #3b82f6);
    }
    
    .donut-label {
      font-size: 0.8rem;
      color: var(--text-muted, #6b7280);
    }
  }
  
  .dark-mode & {
    &::before {
      background-color: var(--dark-bg, #1f2937);
    }
    
    .donut-label {
      color: var(--dark-text-muted, #9ca3af);
    }
  }
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-top: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
  
  .dark-mode & {
    border-color: var(--dark-border-color, #374151);
  }
}

@media (max-width: 768px) {
  .donut-chart {
    width: 140px;
    height: 140px;
    
    .donut-percentage {
      font-size: 1.5rem;
    }
  }
}
</style> 